import React, { useState, useCallback } from 'react'
import { Editor } from '@tiptap/core'
import { Node as ProseMirrorNode } from '@tiptap/pm/model'
import { NodeViewWrapper, NodeViewContent } from '@tiptap/react'
import { IconCategory } from '@deepguide-ai/types'
import { Plus, X, MoreHorizontal } from 'lucide-react'
import { cn } from '@/lib/utils'
import { EmojiPicker } from '@/components/emoji-picker'
import { IconComponent } from '@/components/ui'
import { type IconType } from '@/components/ui'
import { Button } from '@/components/ui'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui'
import { Separator } from '@/components/ui'
import { TabsAttributes } from './tabs-extension'

interface TabsNodeViewProps {
  node: ProseMirrorNode
  editor: Editor
  getPos: () => number | undefined
  updateAttributes: (attributes: Record<string, any>) => void
  deleteNode: () => void
  selected: boolean
}

interface TabData {
  title: string
  icon: string
  iconType: IconType
  iconColor: string
  showIcon: boolean
  iconCategory: IconCategory
}

export function TabsNodeView({
  node,
  editor,
  getPos,
  updateAttributes,
  deleteNode,
  selected,
}: TabsNodeViewProps) {
  const [activeTab, setActiveTab] = useState(0)
  const [isHovered, setIsHovered] = useState(false)
  const [settingsTabIndex, setSettingsTabIndex] = useState<number | null>(null)
  const [editingTabIndex, setEditingTabIndex] = useState<number | null>(null)
  const [editingTitle, setEditingTitle] = useState('')

  const attrs = node.attrs as TabsAttributes

  // Extract tab data from child nodes
  const tabsData: TabData[] = []
  node.content.forEach((childNode, index) => {
    if (childNode.type.name === 'Tab') {
      const tabAttrs = childNode.attrs || {}
      tabsData.push({
        title: tabAttrs.title || `Tab ${index + 1}`,
        icon: tabAttrs.icon || 'sparkles',
        iconType: tabAttrs.iconType || 'REGULAR',
        iconColor: tabAttrs.iconColor || '#3b82f6',
        showIcon: tabAttrs.showIcon || false,
        iconCategory: tabAttrs.iconCategory || 'LUCIDE',
      })
    }
  })

  // Handle tab click
  const handleTabClick = useCallback(
    (index: number, e?: React.MouseEvent) => {
      if (e) e.stopPropagation()

      // If already editing this tab, don't change active tab
      if (editingTabIndex === index) {
        return
      }

      // If clicking on the active tab, start editing
      if (activeTab === index) {
        const tabData = tabsData[index]
        setEditingTabIndex(index)
        setEditingTitle(tabData.title)
        return
      }

      // Otherwise, switch to the tab
      setActiveTab(index)
    },
    [activeTab, editingTabIndex, tabsData]
  )

  // Handle tab title save
  const handleTabTitleSave = useCallback(
    (index: number) => {
      if (!editingTitle.trim()) return

      const pos = getPos()
      if (pos === undefined) return

      // 正确计算 tab 位置：累加前面所有 tab 的 nodeSize
      let tabPos = pos + 1 // 从第一个子节点开始
      for (let i = 0; i < index; i++) {
        tabPos += node.content.child(i).nodeSize
      }

      const transaction = editor.state.tr
      transaction.setNodeMarkup(tabPos, undefined, {
        ...node.content.child(index).attrs,
        title: editingTitle.trim(),
      })
      editor.view.dispatch(transaction)

      setEditingTabIndex(null)
      setEditingTitle('')
    },
    [editor, getPos, node, editingTitle]
  )

  // Handle tab title cancel
  const handleTabTitleCancel = useCallback(() => {
    setEditingTabIndex(null)
    setEditingTitle('')
  }, [])

  // Handle add tab
  const handleAddTab = useCallback(() => {
    const pos = getPos()
    if (pos === undefined) return

    const newTabIndex = tabsData.length
    const newTabPos = pos + node.nodeSize - 1 // Position at the end of tabs

    editor.commands.insertContentAt(newTabPos, {
      type: 'Tab',
      attrs: {
        title: `Tab ${newTabIndex + 1}`,
        icon: 'sparkles',
        iconType: 'REGULAR',
        iconColor: '#3b82f6',
        showIcon: false,
      },
      content: [
        {
          type: 'paragraph',
          content: [],
        },
      ],
    })

    // Switch to the new tab
    setActiveTab(newTabIndex)
  }, [editor, getPos, node.nodeSize, tabsData.length])

  // Handle delete tab
  const handleTabDelete = useCallback(
    (index: number, e: React.MouseEvent) => {
      e.stopPropagation()

      if (tabsData.length <= 1) return // Keep at least one tab

      const pos = getPos()
      if (pos === undefined) return

      // Close settings if deleting the tab that has settings open
      if (settingsTabIndex === index) {
        setSettingsTabIndex(null)
      } else if (settingsTabIndex !== null && settingsTabIndex > index) {
        setSettingsTabIndex(settingsTabIndex - 1)
      }

      // Adjust active tab if needed
      if (activeTab === index) {
        setActiveTab(Math.max(0, index - 1))
      } else if (activeTab > index) {
        setActiveTab(activeTab - 1)
      }

      // Delete the tab
      const tabStartPos = pos + 1
      let tabPos = tabStartPos
      for (let i = 0; i < index; i++) {
        tabPos += node.content.child(i).nodeSize
      }
      const tabEndPos = tabPos + node.content.child(index).nodeSize

      const transaction = editor.state.tr
      transaction.delete(tabPos, tabEndPos)
      editor.view.dispatch(transaction)
    },
    [editor, getPos, node, tabsData.length, activeTab, settingsTabIndex]
  )

  // Handle tab settings
  const handleTabSettings = useCallback(
    (index: number, e: React.MouseEvent) => {
      e.stopPropagation()
      setSettingsTabIndex(settingsTabIndex === index ? null : index)
    },
    [settingsTabIndex]
  )

  // Handle tab settings save
  const handleTabSettingsSave = useCallback(
    (tabIndex: number, newAttributes: Partial<TabData>) => {
      const pos = getPos()
      if (pos === undefined) return

      // 正确计算 tab 位置：累加前面所有 tab 的 nodeSize
      let tabPos = pos + 1 // 从第一个子节点开始
      for (let i = 0; i < tabIndex; i++) {
        tabPos += node.content.child(i).nodeSize
      }

      const currentAttrs = node.content.child(tabIndex).attrs || {}

      const transaction = editor.state.tr
      transaction.setNodeMarkup(tabPos, undefined, {
        ...currentAttrs,
        ...newAttributes,
      })
      editor.view.dispatch(transaction)

      setSettingsTabIndex(null)
    },
    [editor, getPos, node]
  )

  return (
    <NodeViewWrapper
      className={cn(
        'group/tabs relative my-5 p-0.5',
        selected && 'ProseMirror-selectednode'
      )}
      data-type='Tabs'
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className='tabs-container relative overflow-hidden rounded-lg border'>
        {/* Tab Headers */}
        <div
          className='tab-headers flex items-center justify-between gap-0 border-b'
          contentEditable={false}
        >
          <div className='flex w-full items-center justify-between pr-4'>
            <div className='flex items-center'>
              {tabsData.map((tabData, index) => (
                <div key={index} className={`relative`}>
                  <div
                    onClick={(e) => handleTabClick(index, e)}
                    className={`group/tab relative flex min-w-[120px] cursor-pointer items-center px-4 py-2.5 text-sm font-medium transition-colors ${
                      activeTab === index
                        ? '-mb-px border-b-2 border-blue-500 text-blue-600'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {/* Left-aligned content container */}
                    <div className='flex flex-1 items-center gap-2'>
                      {/* Show icon if enabled */}
                      {tabData.showIcon && (
                        <IconComponent
                          iconName={tabData.icon}
                          iconType={tabData.iconType}
                          iconColor={tabData.iconColor}
                          iconCategory={tabData.iconCategory}
                          size={16}
                          className={`transition-colors ${
                            tabData.iconCategory === 'EMOJI' ? 'h-4 w-4' : ''
                          }`}
                        />
                      )}

                      {editor.isEditable && editingTabIndex === index ? (
                        <input
                          type='text'
                          value={editingTitle}
                          onChange={(e) => setEditingTitle(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleTabTitleSave(index)
                            } else if (e.key === 'Escape') {
                              handleTabTitleCancel()
                            }
                          }}
                          onBlur={() => handleTabTitleSave(index)}
                          autoFocus
                          className='flex-1 bg-transparent text-sm font-medium outline-none'
                        />
                      ) : (
                        <span className='text-left'>{tabData.title}</span>
                      )}
                    </div>

                    {/* Tab controls - always visible, positioned at the end */}
                    {editor.isEditable && (
                      <div className='ml-4'>
                        {/* Settings and actions dropdown */}
                        <Popover
                          open={settingsTabIndex === index}
                          onOpenChange={(open) =>
                            setSettingsTabIndex(open ? index : null)
                          }
                        >
                          <PopoverTrigger asChild>
                            <Button
                              variant='ghost'
                              size='menuAction'
                              className='h-6 w-6 p-0'
                              onClick={(e) => handleTabSettings(index, e)}
                            >
                              <MoreHorizontal className='h-4 w-4' />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className='w-80 p-4' align='end'>
                            <TabSettings
                              tabData={tabData}
                              onSave={(newAttributes) =>
                                handleTabSettingsSave(index, newAttributes)
                              }
                              onCancel={() => setSettingsTabIndex(null)}
                              onDelete={
                                tabsData.length > 1
                                  ? () => {
                                      setSettingsTabIndex(null)
                                      handleTabDelete(index, {
                                        stopPropagation: () => {},
                                      } as React.MouseEvent)
                                    }
                                  : undefined
                              }
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Add new tab button */}
            {editor.isEditable && (
              <Button variant='ghost' size='menuAction' onClick={handleAddTab}>
                <Plus className='h-4 w-4' />
              </Button>
            )}
          </div>
        </div>

        {/* Tab Content */}
        <div className='tab-content p-4'>
          <style key={`tab-style-${activeTab}`}>{`
            /* Hide all tabs first */
            .tabs-container .tab-content .react-renderer.node-Tab {
              display: none !important;
            }
            /* Show only the active tab */
            .tabs-container .tab-content .react-renderer.node-Tab:nth-child(${activeTab + 1}) {
              display: block !important;
            }
            /* Remove individual tab styling */
            .tabs-container .tab-content .tab-element {
              border: none !important;
              padding: 0 !important;
              margin: 0 !important;
            }
          `}</style>
          <NodeViewContent />
        </div>
      </div>
    </NodeViewWrapper>
  )
}

// Tab Settings Component
interface TabSettingsProps {
  tabData: TabData
  onSave: (attributes: Partial<TabData>) => void
  onCancel: () => void
  onDelete?: () => void
}

function TabSettings({
  tabData,
  onSave,
  onCancel,
  onDelete,
}: TabSettingsProps) {
  const [tempTitle, setTempTitle] = useState(tabData.title)
  const [tempIcon, setTempIcon] = useState(tabData.icon)
  const [tempIconType] = useState(tabData.iconType)
  const [tempIconColor, setTempIconColor] = useState(tabData.iconColor)
  const [tempShowIcon, setTempShowIcon] = useState(tabData.showIcon)
  const [tempIconCategory, setTempIconCategory] = useState(tabData.iconCategory)

  const handleSave = () => {
    const newAttributes: Partial<TabData> = {}

    if (tempTitle.trim() !== tabData.title) {
      newAttributes.title = tempTitle.trim()
    }

    if (tempIcon !== tabData.icon) {
      newAttributes.icon = tempIcon
    }

    if (tempIconType !== tabData.iconType) {
      newAttributes.iconType = tempIconType
    }

    if (tempIconColor !== tabData.iconColor) {
      newAttributes.iconColor = tempIconColor
    }

    if (tempShowIcon !== tabData.showIcon) {
      newAttributes.showIcon = tempShowIcon
    }

    if (tempIconCategory !== tabData.iconCategory) {
      newAttributes.iconCategory = tempIconCategory
    }
    onSave(newAttributes)
  }

  return (
    <div>
      {/* Header */}
      <div className='-mx-4 mb-4 flex items-center justify-between border-b border-sidebar-border px-4 pb-3'>
        <h3 className='font-[Fraunces] text-[20px] leading-[1.2] font-medium text-sidebar-foreground'>
          Tab Settings
        </h3>

        <Button
          variant='ghost'
          size='menuAction'
          onClick={onCancel}
          className='h-8 w-8 text-[#60646D] hover:bg-muted hover:text-[#43464D]'
        >
          <X className='h-4 w-4' />
        </Button>
      </div>

      {/* Title Input */}
      <div className='mb-4 flex flex-col gap-2'>
        <label className='text-[13px] leading-[1.2] font-normal text-[#1F2024]'>
          Tab Title
        </label>
        <div className='flex h-9 items-center gap-2'>
          <div className='flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-[#E3E1DD] bg-white'>
            <EmojiPicker
              selectedIcon={tempShowIcon ? tempIcon : undefined}
              iconColor={tempIconColor}
              iconCategory={tempIconCategory}
              onEmojiSelect={(emoji) => {
                setTempShowIcon(true)
                setTempIcon(emoji)
                setTempIconCategory('EMOJI')
              }}
              onIconChange={(iconName: string, iconCategory: IconCategory) => {
                if (iconName === '') {
                  setTempIconCategory('LUCIDE')
                  setTempShowIcon(false)
                  return
                }
                setTempShowIcon(true)
                setTempIcon(iconName)
                setTempIconCategory(iconCategory)
              }}
              onColorChange={setTempIconColor}
              isPopoverMode={true}
            />
          </div>
          <input
            type='text'
            value={tempTitle}
            onChange={(e) => setTempTitle(e.target.value)}
            placeholder='Enter tab title...'
            className='placeholder:text-[#85878D] focus-visible:ring-ring h-9 w-full flex-1 rounded-md border border-[#E3E1DD] px-3 text-[13px] leading-[1.2] font-normal text-[#43464D] transition-colors focus-visible:ring-1 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50'
          />
        </div>
      </div>

      {/* <Separator className='my-4' /> */}

      {/* Icon Settings */}
      {/* <div className='space-y-5'>
        <div className='flex justify-between items-center'>
          <label className='text-xs leading-4 text-muted-foreground'>
            Show Icon
          </label>
          <div onClick={(e) => e.stopPropagation()}>
            <Switch checked={tempShowIcon} onCheckedChange={setTempShowIcon} />
          </div>
        </div>

        {tempShowIcon && (
          <div onClick={(e) => e.stopPropagation()}>
            <IconEditor
              selectedIcon={tempIcon}
              iconType={tempIconType}
              iconColor={tempIconColor}
              onIconChange={setTempIcon}
              onTypeChange={setTempIconType}
              onColorChange={setTempIconColor}
            />
          </div>
        )}
      </div> */}
      {/* 
      <div className='flex flex-col gap-2' onClick={(e) => e.stopPropagation()}>
        <label className='text-muted-foreground text-xs leading-4'>Icon</label>
        <EmojiPicker
          selectedIcon={tempShowIcon ? tempIcon : undefined}
          iconColor={tempIconColor}
          iconCategory={tempIconCategory}
          onEmojiSelect={(emoji) => {
            setTempShowIcon(true)
            setTempIcon(emoji)
            setTempIconCategory('EMOJI')
          }}
          onIconChange={(iconName: string, iconCategory: IconCategory) => {
            if (iconName === '') {
              setTempIconCategory('LUCIDE')
              setTempShowIcon(false)
              return
            }
            setTempShowIcon(true)
            setTempIcon(iconName)
            setTempIconCategory(iconCategory)
          }}
          onColorChange={setTempIconColor}
          isPopoverMode={true}
        />
      </div> */}

      <Separator className='m-0 -ml-4 bg-sidebar-border !w-[calc(100%+32px)]' />

      {/* Action Buttons */}
      <div className='flex items-center justify-between pt-[14px]'>
        {/* Delete button on the left */}
        <div>
          {onDelete && (
            <Button
              variant='ghost'
              size='sm'
              onClick={onDelete}
              className='h-9 bg-transparent px-0 text-[13px] leading-[1.1] font-normal text-[#C24646] hover:bg-transparent hover:text-[#C24646]'
            >
              Delete
            </Button>
          )}
        </div>

        {/* Save/Cancel buttons on the right */}
        <div className='flex items-center space-x-2'>
          <Button
            variant='outline'
            size='sm'
            onClick={onCancel}
            className='h-9 w-24 rounded-lg border-[#E3E1DD] px-0 text-[13px] text-[#43464D] hover:bg-muted'
          >
            Cancel
          </Button>
          <Button
            size='sm'
            onClick={handleSave}
            className='h-9 w-[120px] rounded-lg bg-sidebar-primary px-0 text-[13px] text-white hover:bg-[#6f8264]'
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  )
}
